import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {pathToURL} from 'BenchlingUtil';
import FormattersConstants from 'notebook/mediocre/formatted-text/FormattersConstants';
import InsertLinkModal, {Link} from 'notebook/mediocre/hyperlinks/InsertLinkModal';
import {FormatToggleButton} from 'notebook/tables/toolbar/FormatToggleButton';
import ModalAnchor from 'ui-library/ModalAnchor';
import {assertNever, Optional} from 'util/Types';

interface LinkModalButtonProps {
  link: {
    text?: string;
    url?: Optional<string>;
  };
  onChange: (link: {text?: string; url?: string}) => void;
  disabled: boolean;
}

export function LinkModalButton({link, onChange, disabled}: LinkModalButtonProps): JSX.Element {
  const [showSetLinkModal, setShowSetLinkModal] = useState(false);

  const extractLinkDetails = (link: Link): {url?: string; text: string} => {
    switch (link.type) {
      case 'external':
        return {url: link.url ?? undefined, text: link.text};
      case 'internal':
        assertAndContinue(link.url != null, 'Internal link has no URL');
        return {url: link.url != null ? pathToURL(link.url) : undefined, text: link.name};
      default:
        return assertNever(link);
    }
  };

  const handleSetLink = (newLink: Link): void => {
    if (newLink == null) {
      setShowSetLinkModal(false);
      return;
    }
    setShowSetLinkModal(false);
    onChange(extractLinkDetails(newLink));
  };

  return (
    <>
      <FormatToggleButton
        active={Boolean(link.url)}
        format={FormattersConstants.LINK}
        tooltip="Hyperlink"
        icon="format-link"
        disabled={disabled}
        onClick={() => {
          setShowSetLinkModal(true);
        }}
      />
      {showSetLinkModal && (
        <ModalAnchor open={showSetLinkModal}>
          <InsertLinkModal
            open={showSetLinkModal}
            initialText={link.text}
            initialUrl={link.url}
            setLinkAndClose={handleSetLink}
          />
        </ModalAnchor>
      )}
    </>
  );
}
